import React, { useEffect, useRef, useState } from 'react'
import { Button, Modal, notification, Row } from 'antd'
import { useDispatch } from 'react-redux'
import { unwrapResult } from '@reduxjs/toolkit'
import { EditableList } from '../../../compnents/list/EditableList'
import { TreatmentBillingCategories, BillingCategoryName } from '../../../models/billingCategory'
import { AddTreatment } from './AddTreatment'
import { getMemberInterestPageAsync, submitMemberInterestBatchAsync, getMemberInterestExceptionPageAsync } from '../memberBenefitsSlice'
import { RootDispatch } from '../../../app/store'

interface AddTreatmentCategoryProps {
  visible: boolean
  onCancel: () => void
  onOk: (data: any[]) => void
  onRefresh?: () => void
}

export const AddTreatmentCategory = ({ visible, onCancel, onOk, onRefresh }: AddTreatmentCategoryProps) => {
  const dispatch = useDispatch<RootDispatch>()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<any[]>(() =>
    TreatmentBillingCategories.map((v) => ({
      billingCategory: v,
      discountType: 0,
      price: null,
      specialNum: 0,
    }))
  )

  const loadData = () => {
    setLoading(true)
    dispatch(getMemberInterestPageAsync({ billingCategoryFlag: true, size: 10000 }))
      .then(unwrapResult)
      .then((res: any) => {
        const records: any[] = res?.records || res || []
        const savedSet = new Set(records.map((r: any) => r.billingCategory))

        if (isExceptionSavedRef.current) {
          // 已保存过分类，只展示首次打开时的那几种，并回填已保存数据
          setData(
            initialCategoriesRef.current.map((v) => {
              const saved = records.find((r: any) => r.billingCategory === v)
              return {
                billingCategory: v,
                discountType: 0,
                price: saved?.price ?? null,
                specialNum: saved?.specialNum ?? 0,
              }
            })
          )
        } else {
          // 只展示未保存过的分类
          const filtered = TreatmentBillingCategories.filter((v) => !savedSet.has(v))
          initialCategoriesRef.current = filtered
          setData(
            filtered.map((v) => ({
              billingCategory: v,
              discountType: 0,
              price: null,
              specialNum: 0,
            }))
          )
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    if (visible) {
      isExceptionSavedRef.current = false
      initialCategoriesRef.current = []
      loadData()
    }
  }, [visible])

  const [exceptionVisible, setExceptionVisible] = useState(false)
  const [exceptionRecord, setExceptionRecord] = useState<any>(null)
  const [exceptionDefaultKeys, setExceptionDefaultKeys] = useState<React.Key[]>([])
  const [exceptionDefaultRows, setExceptionDefaultRows] = useState<any[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [submitLoading, setSubmitLoading] = useState(false)
  const isExceptionSavedRef = useRef(false)
  const initialCategoriesRef = useRef<string[]>([])
  const dataRef = useRef(data)
  dataRef.current = data

  const handleOk = () => {
    setSubmitLoading(true)
    setTimeout(() => {
      const currentData = dataRef.current
      const selectedData = currentData.filter((row: any) => selectedRowKeys.includes(row.billingCategory))
      if (selectedData.length === 0) {
        notification.warning({ message: '请至少选择一项' })
        setSubmitLoading(false)
        return
      }

      const params = selectedData.map((row: any) => ({
        name: BillingCategoryName(row.billingCategory),
        billingCategory: row.billingCategory,
        price: (row.price == null || row.price === '') ? 100 : row.price,
      }))

      dispatch(submitMemberInterestBatchAsync(params))
        .then(unwrapResult)
        .then(() => {
          notification.success({ message: '保存成功' })
          handleCancel()
        })
        .finally(() => {
          setSubmitLoading(false)
        })
    }, 500)
  }

  const handleCancel = () => {
    setSelectedRowKeys([])
    onCancel()
  }

  const columns = [
    {
      title: '开票项目',
      dataIndex: 'billingCategory',
      align: 'center' as const,
      key: 'billingCategory',
      render: (_: any, r: any) => BillingCategoryName(r.billingCategory),
    },
    {
      title: '折扣类型',
      dataIndex: 'discountType',
      align: 'center' as const,
      key: 'discountType',
      width: '8rem',
      render: () => '折扣',
    },
    {
      title: '会员价',
      dataIndex: 'price',
      align: 'center' as const,
      key: 'price',
      width: '5rem',
      colSpan: 2,
      className: 'noRightBorder noRowSelect',
      editable: (r: any) => selectedRowKeys.includes(r.billingCategory),
      rules: [
        {
          required: true,
          message: '请输入会员价',
        },
        {
          pattern: new RegExp(/^(100|[1-9]?\d)$/),
          message: '折扣请输入0-100的整数',
        },
      ],
      onSave: (prev: any, next: any) => {
        const price = next.price
        const val = Number(price)
        if (val < 0 || val > 100) {
          return
        }
        setData((d) =>
          d.map((item) =>
            item.billingCategory === prev.billingCategory ? { ...item, price: price || null } : item
          )
        )
      },
      render: (_: any, r: any) => {
        if (r.price == null) return ''
        const num = parseFloat(r.price)
        return String(Math.round(num))
      },
    },
    {
      dataIndex: 'unit',
      align: 'center' as const,
      key: 'unit',
      width: '4rem',
      colSpan: 0,
      className: 'noRowSelect',
      render: function UnitCell(_: any, r: any) {
        const selected = selectedRowKeys.includes(r.billingCategory)
        return <span style={{ color: selected ? undefined : '#bbb' }}>%</span>
      },
    },
    // {
    //   title: '例外',
    //   dataIndex: 'specialNum',
    //   align: 'center' as const,
    //   key: 'specialNum',
    //   width: '6rem',
    //   render: (_: any, r: any) => `例外 ${r.specialNum || 0} 个`,
    // },
    /*
    {
      title: '操作',
      align: 'center' as const,
      key: 'action',
      width: '6rem',
      render: function ActionCell(_: any, r: any) {
        if (!selectedRowKeys.includes(r.billingCategory)) return null
        return (
          <Button type="link" onClick={() => {
            setExceptionRecord(r)
            dispatch(getMemberInterestExceptionPageAsync({ billingCategory: r.billingCategory, catalogType: 1 }))
              .then(unwrapResult)
              .then((res: any) => {
                const records: any[] = res?.records || res || []
                setExceptionDefaultKeys(records.map((v: any) => v.tenantCatalogId))
                setExceptionDefaultRows(records.map((v: any) => ({
                  id: v.tenantCatalogId,
                  name: v.name,
                  retailPrice: v.retailPrice,
                  stockPrice: v.stockPrice,
                  spec: v.spec,
                  catalogType: v.catalogType ?? -1,
                  discountType: v.discountType ?? 0,
                  price: v.price,
                  totalNumStr: v.totalNumStr,
                })))
                setExceptionVisible(true)
              })
          }}>
            编辑例外
          </Button>
        )
      },
    },
    */
  ]

  return (
    <>
    <Modal
      title="添加诊疗分类"
      visible={visible}
      maskClosable={false}
      onCancel={handleCancel}
      onOk={handleOk}
      confirmLoading={submitLoading}
      width={800}
      destroyOnClose
    >
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <span>选择开票项目</span>
        <span>已选择 {selectedRowKeys.length} 项</span>
      </Row>
      <EditableList
        loading={loading}
        rowKey={(t: any) => t.billingCategory}
        bordered
        dataSource={data}
        pagination={false}
        style={{ height: 500 }}
        columns={columns}
        onRow={(record: any) => ({
          onClick: (e: React.MouseEvent) => {
            // 点击可编辑单元格区域时不触发行选中切换
            const target = e.target as HTMLElement
            if (target.closest('input') || target.closest('textarea') || target.closest('[data-field]') || target.closest('.noRowSelect')) {
              return
            }
            setSelectedRowKeys((prev) => {
              const key = record.billingCategory
              if (prev.includes(key)) {
                return prev.filter((k) => k !== key)
              }
              return [...prev, key]
            })
          },
        })}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
        }}
      />
    </Modal>
    <AddTreatment
      visible={exceptionVisible}
      title={`编辑例外诊疗 - ${exceptionRecord ? BillingCategoryName(exceptionRecord.billingCategory) : ''}`}
      mode="exception"
      billingCategory={exceptionRecord?.billingCategory}
      defaultSelectedRowKeys={exceptionDefaultKeys}
      defaultSelectedRows={exceptionDefaultRows}
      onCancel={() => {
        setExceptionVisible(false)
        setExceptionDefaultKeys([])
        setExceptionDefaultRows([])
      }}
      onOk={() => {
        const categoryRow = data.find((item) => item.billingCategory === exceptionRecord?.billingCategory)
        if (categoryRow) {
          if (isExceptionSavedRef.current) {
            // 已保存过分类，不再重复调保存接口
            loadData()
            onRefresh?.()
          } else {
            dispatch(submitMemberInterestBatchAsync([{
              name: BillingCategoryName(categoryRow.billingCategory),
              billingCategory: categoryRow.billingCategory,
              price: (categoryRow.price == null || categoryRow.price === '') ? 100 : categoryRow.price,
            }]))
              .then(unwrapResult)
              .then(() => {
                isExceptionSavedRef.current = true
                loadData()
                onRefresh?.()
              })
          }
        }
        setExceptionVisible(false)
        setExceptionDefaultKeys([])
        setExceptionDefaultRows([])
      }}
    />
    </>
  )
}
